import { createContainerDiv, createTicks, createBars, createMasks, createPeakLabels, paintMeter } from './markup.js';
import { calculateMaxValues } from './peak-sample.js';
import { calculateTPValues } from './true-peak.js';
import { findAudioProcBufferSize } from './utils.js';

const defaultConfig = {
  borderSize: 2,
  fontSize: 14,
  backgroundColor: 'black',
  tickColor: 'var(--cf)',
  labelColor: 'var(--cf)',
  gradient: ['var(--c8) 1%', 'var(--ca) 16%', 'var(--c6) 45%', 'var(--c3) 100%'],
  dbRange: 48,
  dbTickSize: 6,
  maskTransition: '0.1s',
  audioMeterStandard: 'peak-sample', // Could be "true-peak" (ITU-R BS.1770) or "peak-sample"
  refreshEveryApproxMs: 20,
  peakHoldDuration: null,
};

export function createMeterNode(sourceNode, audioCtx, options = {}) {
  const config = Object.assign({}, defaultConfig, options);
  const { refreshEveryApproxMs } = config;
  const { channelCount, sampleRate } = sourceNode;

  const resfreshIntervalSamples = (refreshEveryApproxMs / 1000) * sampleRate * channelCount;
  const bufferSize = findAudioProcBufferSize(resfreshIntervalSamples);
  const meterNode = audioCtx.createScriptProcessor(bufferSize, channelCount, channelCount);
  sourceNode.connect(meterNode).connect(audioCtx.destination);
  return meterNode;
}

export function updateMeter(audioProcessingEvent, config, meterData) {
  const { inputBuffer } = audioProcessingEvent;
  const { audioMeterStandard, peakHoldDuration } = config;
  let channelMaxes = [];

  if (audioMeterStandard === 'true-peak') {
    channelMaxes = calculateTPValues(inputBuffer, meterData);
  } else {
    channelMaxes = calculateMaxValues(inputBuffer);
  }
  for (let i = 0; i < channelMaxes.length; i += 1) {
    meterData.tempPeaks[i] = channelMaxes[i];
    if (channelMaxes[i] > meterData.heldPeaks[i]) {
      meterData.heldPeaks[i] = channelMaxes[i];
      if (peakHoldDuration) {
        if (meterData.peakHoldTimeouts[i]) {
          clearTimeout(meterData.peakHoldTimeouts[i]);
        }
        meterData.peakHoldTimeouts[i] = setTimeout(() => {
          meterData.heldPeaks[i] = meterData.tempPeaks[i];
        }, peakHoldDuration);
      }
    }
  }
}

export function createMeter(domElement, meterNode, options = {}) {
  const config = Object.assign({}, defaultConfig, options);

  const meterElement = createContainerDiv(domElement, config);
  const meterData = createTicks(meterElement, config);

  const { channelCount } = meterNode;

  meterData.tempPeaks = new Array(channelCount).fill(0.0);
  meterData.heldPeaks = new Array(channelCount).fill(0.0);
  meterData.peakHoldTimeouts = new Array(channelCount).fill(null);
  meterData.channelCount = channelCount;

  meterData.channelBars = createBars(meterElement, config, meterData);
  meterData.channelMasks = createMasks(meterElement, config, meterData);
  meterData.textLabels = createPeakLabels(meterElement, config, meterData);

  if (config.audioMeterStandard === 'true-peak') {
    meterData.lpfCoefficients = [];
    meterData.lpfBuffer = [];
    meterData.upsampleFactor = 4;
    meterData.lastChannelTP = [];
    meterData.decayFactor = 0.99999;
  }

  meterNode.onaudioprocess = (evt) => updateMeter(evt, config, meterData);
  meterElement.addEventListener('click', () => {
    meterData.heldPeaks.fill(0.0);
  }, false);
  paintMeter(config, meterData);
}
